import { acceptHMRUpdate } from "pinia"
import { defineEntryListStore, FunctionOrHandlerObject } from "@/store/factories/entryListStore"
import { api } from "@/logic/api"
import { useAppConfigStore } from "@/store/appConfig"
import { getConstants } from "@/logic/constants"

export type TranslationsListEntry = {
  id: number
  type: number
  unique_id: string
  is_translatable: boolean
  title: string
  status: "missing" | "dependenciesMissing" | "ok"
  updated_at: string
  autotranslation_running: boolean
  nesting_meta: {
    has_children: boolean
    indentation: number
    parent_unique_id?: string
    has_children_matching_search_query: boolean
  }
}

type TranslationStatus = {
  foreign_type: number
  foreign_id: number
  autotranslation_running_since: string
}

type AutotranslationCounts = {
  humanTranslationsCount: number
  totalTranslationsCount: number
}

export const useTranslationsStore = defineEntryListStore("translations", {
  state: () => {
    return {
      entries: null as null | TranslationsListEntry[],
      totalCount: null as null | number,
      missingTranslationsCount: 0 as number,
      translationStatuses: null as null | TranslationStatus[],
      translationStatusesLanguage: null as null | string,
      translationStatusAbortController: null as AbortController | null,
      autotranslateAbortController: new AbortController(),
      isAutoTranslateRequestRunning: false,
      isLoading: true,
      pagination: {
        page: 1,
        totalCount: 0,
        perPage: 20,
      },
      sort: {
        by: null,
        desc: false,
      },
      filters: [],
      tags: [],
      searchQuery: "",
      language: "" as string,
      types: [] as number[],
      translationStatus: "" as "" | "notTranslated" | "onlyTranslated",
      dataLoadedCallbacks: [] as FunctionOrHandlerObject[],
      expansionState: {} as Record<string, boolean>,
      questionCategories: [] as number[],
      questionFilters: [] as string[],
      courseFilters: [] as string[],
    }
  },
  getters: {
    configWatcher(): Array<any> {
      return [this.language, this.pagination.page, this.pagination.perPage, this.filters, this.tags, this.searchQuery, this.sort.by, this.sort.desc, this.types, this.translationStatus, this.questionCategories, this.questionFilters, this.courseFilters]
    },
    primaryLanguageActive(): boolean {
      const appConfigStore = useAppConfigStore()
      return appConfigStore.settings.defaultLanguage === this.language
    },
    entryListRequestParams(): Record<string, any> {
      const params: Record<string, any> = {
        language: this.language,
        page: this.pagination.page,
        perPage: this.pagination.perPage,
        filters: this.filters,
        tags: this.tags,
        searchQuery: this.searchQuery,
        sort: this.sort,
        types: this.types,
        onlyMissing: this.translationStatus === "notTranslated",
        onlyTranslated: this.translationStatus === "onlyTranslated",
      }
      if (this.types.includes(getConstants().MORPH_TYPES.QUESTION)) {
        params.questionCategories = this.questionCategories
        params.questionFilters = this.questionFilters
      }
      if (this.types.includes(getConstants().MORPH_TYPES.COURSE)) {
        params.courseFilters = this.courseFilters
      }
      return params
    },
    selectedLanguageTranslationStatuses(): null | TranslationStatus[] {
      if (this.translationStatusesLanguage !== this.language) {
        return null
      }
      return this.translationStatuses
    },
    isLoadingTranslationStatuses(): boolean {
      return this.translationStatusAbortController !== null
    },
    translationStatusByTypeId(): Record<string, TranslationStatus> {
      if (!this.selectedLanguageTranslationStatuses) {
        return {}
      }
      const statuses: Record<string, TranslationStatus> = {}
      this.selectedLanguageTranslationStatuses.forEach((status) => {
        statuses[status.foreign_type + "-" + status.foreign_id] = status
      })

      return statuses
    },
    isAutotranslationRunningForEntry(): (entry: TranslationsListEntry) => boolean {
      const runningStatusesByTypeId = this.translationStatusByTypeId
      return (entry) => {
        if (runningStatusesByTypeId[`${entry.type}-${entry.id}`]) {
          return true
        }

        const childEntries = this.childrenByUniqueId[entry.unique_id]
        if (!childEntries) {
          return false
        }

        return childEntries.some((childEntry) => Boolean(runningStatusesByTypeId[`${childEntry.type}-${childEntry.id}`]))
      }
    },
    entriesByUniqueId(): Record<string, TranslationsListEntry> {
      const entriesByUniqueId: Record<string, TranslationsListEntry> = {}
      if (!this.entries) {
        return entriesByUniqueId
      }
      this.entries.forEach((row) => {
        entriesByUniqueId[row.unique_id] = row
      })
      return entriesByUniqueId
    },
    childrenByUniqueId(): Record<string, TranslationsListEntry[]> {
      if (!this.entries) {
        return {}
      }
      /**
       * This is a relatively unoptimized, but simple way to get a list of all the children (including children's children)
       * for each unique parent id
       */
      // First, create a list of all the direct children of each row
      // We need that list in the addChildrenToParent function.
      const shallowChildrenByParent: Record<string, TranslationsListEntry[]> = {}
      this.entries.forEach((row) => {
        const parentUniqueId = row.nesting_meta.parent_unique_id
        if (!parentUniqueId) {
          return
        }
        if (shallowChildrenByParent[parentUniqueId] === undefined) {
          shallowChildrenByParent[parentUniqueId] = []
        }
        shallowChildrenByParent[parentUniqueId].push(row)
      })

      // Now create the final list that contains all deep children for all rows
      const childrenByParent: Record<string, TranslationsListEntry[]> = {}
      const addChildrenToParent = (row: TranslationsListEntry, parentUniqueId: string): void => {
        if (!row.nesting_meta.has_children) {
          return
        }
        const children = shallowChildrenByParent[row.unique_id]
        if (children === undefined) {
          return
        }
        children.forEach((child) => {
          childrenByParent[parentUniqueId].push(child)
          addChildrenToParent(child, parentUniqueId)
        })
      }
      this.entries.forEach((row) => {
        const parentUniqueId = row.nesting_meta.parent_unique_id
        if (parentUniqueId === undefined) {
          return
        }
        if (childrenByParent[parentUniqueId] === undefined) {
          childrenByParent[parentUniqueId] = []
        }
        childrenByParent[parentUniqueId].push(row)
        addChildrenToParent(row, parentUniqueId)
      })
      return childrenByParent
    },
  },
  actions: {
    cancelAutotranslationRequest(): void {
      this.autotranslateAbortController.abort()
      this.isAutoTranslateRequestRunning = false
    },
    async checkAutotranslation(models: Pick<TranslationsListEntry, "type" | "id">[]): Promise<AutotranslationCounts | null> {
      const abortController = this.autotranslateAbortController
      if (this.isAutoTranslateRequestRunning || abortController.signal.aborted) {
        return null
      }
      this.isAutoTranslateRequestRunning = true
      try {
        const response = await api.post("/translations/get-autotranslate-status", { targetLanguage: this.language, models }, abortController)
        return abortController.signal.aborted ? null : response
      } finally {
        if (this.autotranslateAbortController === abortController) {
          this.isAutoTranslateRequestRunning = false
        }
      }
    },
    async startAutotranslation(models: Pick<TranslationsListEntry, "type" | "id">[], overwriteHumanTranslations: boolean): Promise<boolean> {
      const abortController = this.autotranslateAbortController
      if (this.isAutoTranslateRequestRunning || abortController.signal.aborted) {
        return false
      }
      this.isAutoTranslateRequestRunning = true
      try {
        await api.post("/translations/autotranslate", { targetLanguage: this.language, models, overwriteHumanTranslations }, abortController)
        return !abortController.signal.aborted
      } finally {
        if (this.autotranslateAbortController === abortController) {
          this.isAutoTranslateRequestRunning = false
        }
      }
    },
    cancelTranslationStatusRequest(): void {
      this.translationStatusAbortController?.abort()
      this.translationStatusAbortController = null
    },
    async refreshTranslationStatuses(): Promise<void> {
      this.translationStatuses = null
      this.translationStatusesLanguage = null
      await this.updateTranslationStatuses()
    },
    async updateTranslationStatuses(): Promise<void> {
      this.cancelTranslationStatusRequest()
      const abortController = new AbortController()
      this.translationStatusAbortController = abortController
      const targetLanguage = this.language
      try {
        const response = await api.get("/translations/translation-status", { targetLanguage }, abortController)
        if (abortController.signal.aborted || this.language !== targetLanguage) {
          return
        }
        this.translationStatuses = response.statuses
        this.translationStatusesLanguage = targetLanguage
      } catch (error) {
        if (!abortController.signal.aborted && this.language === targetLanguage) {
          throw error
        }
      } finally {
        if (!abortController.signal.aborted) {
          this.translationStatusAbortController = null
        }
      }
    },
  },
})

if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.accept(acceptHMRUpdate(useTranslationsStore, import.meta.hot))
}
